import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getUser, storeUser } from "../../../../shared/data/storage/authStorage";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { changePasswordApi } from "../../data/api/passwordApi";
import { FeatherLock, FeatherEye, FeatherEyeOff } from "@subframe/core";

export const PasswordChangePage: React.FC = () => {
  const { user, isMasterAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{
    current_password?: string;
    new_password?: string;
    confirm_password?: string;
    general?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!currentPassword) {
      newErrors.current_password = "Current password is required";
    }

    if (!newPassword) {
      newErrors.new_password = "New password is required";
    } else if (newPassword.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      newErrors.confirm_password = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await changePasswordApi({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      // Update stored user data to reflect password change
      const storedUser = getUser();
      if (storedUser) {
        storeUser({
          ...storedUser,
          passwordChangedAt: new Date().toISOString(),
        });
      }

      // Password changed successfully - redirect based on role
      if (isMasterAdmin()) {
        navigate("/admin/dashboard");
      } else {
        navigate("/admin/companies/select");
      }
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to change password. Please try again.";
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-center justify-center p-8">
        <div className="flex w-full max-w-[448px] flex-col items-start gap-8">
          <div className="flex w-full flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Change Password
            </span>
            <span className="text-body font-body text-subtext-color">
              Please change your password to continue
            </span>
          </div>

          {errors.general && (
            <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm font-medium text-red-800">
                {errors.general}
              </span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col items-start justify-center gap-4"
          >
            <TextField
              className="h-auto w-full flex-none"
              label="Current Password"
              helpText={errors.current_password || ""}
              icon={<FeatherLock />}
            >
              <div className="relative w-full">
                <TextField.Input
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Enter your current password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (errors.current_password) {
                      setErrors((prev) => ({
                        ...prev,
                        current_password: undefined,
                      }));
                    }
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-subtext-color hover:text-default-font"
                >
                  {showCurrentPassword ? (
                    <FeatherEyeOff className="h-4 w-4" />
                  ) : (
                    <FeatherEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              label="New Password"
              helpText={errors.new_password || ""}
              icon={<FeatherLock />}
            >
              <div className="relative w-full">
                <TextField.Input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errors.new_password) {
                      setErrors((prev) => ({ ...prev, new_password: undefined }));
                    }
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-subtext-color hover:text-default-font"
                >
                  {showNewPassword ? (
                    <FeatherEyeOff className="h-4 w-4" />
                  ) : (
                    <FeatherEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              label="Confirm New Password"
              helpText={errors.confirm_password || ""}
              icon={<FeatherLock />}
            >
              <div className="relative w-full">
                <TextField.Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirm_password) {
                      setErrors((prev) => ({
                        ...prev,
                        confirm_password: undefined,
                      }));
                    }
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-subtext-color hover:text-default-font"
                >
                  {showConfirmPassword ? (
                    <FeatherEyeOff className="h-4 w-4" />
                  ) : (
                    <FeatherEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </TextField>

            <Button
              type="submit"
              className="h-8 w-full flex-none"
              disabled={loading}
              loading={loading}
            >
              Change Password
            </Button>
          </form>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

